import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { ConversationMessage } from "@/lib/types";
import { Skeleton } from "./ui/skeleton";
import { Empty } from "./ui/empty";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";
import { dollars } from "@/lib/money";
import { relTime } from "@/lib/time";
import { cn } from "@/lib/utils";
import { Sparkles } from "lucide-react";

export function ConversationView({
  creatorId,
  memberId,
}: {
  creatorId: string;
  memberId: string;
}) {
  const q = useQuery({
    queryKey: ["thread", creatorId, memberId],
    queryFn: () => api.thread(creatorId, memberId, { limit: 200 }),
  });

  if (q.isLoading) {
    return (
      <div className="space-y-2 p-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton
            key={i}
            className={cn("h-10 w-2/3", i % 2 === 0 ? "self-start" : "ml-auto")}
          />
        ))}
      </div>
    );
  }
  if (q.isError) {
    return <Empty title="Couldn't load thread" hint={String(q.error)} />;
  }

  const items = (q.data?.items ?? []) as ConversationMessage[];
  if (!items.length) {
    return <Empty title="No messages yet" />;
  }

  // Backend returns newest→oldest; render oldest-first inside chrono blocks
  const ordered = [...items].reverse();

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 space-y-2 overflow-y-auto p-3">
        {ordered.map((m) => (
          <Bubble key={m.id} m={m} />
        ))}
      </div>
    </div>
  );
}

function Bubble({ m }: { m: ConversationMessage }) {
  const isMember = m.from_member;
  const isDraftDispatched = !!m.draft_id;
  return (
    <div className={cn("flex gap-2", !isMember && "justify-end")}>
      <div
        className={cn(
          "group max-w-[70%] rounded-lg px-3 py-1.5 text-sm leading-relaxed",
          isMember
            ? "bg-muted text-foreground/90"
            : "bg-brand/15 text-foreground ring-1 ring-inset ring-brand/30",
          isDraftDispatched &&
            "ring-2 ring-inset ring-brand/40 shadow-[0_0_0_1px_hsl(var(--brand)/0.25)]",
        )}
      >
        <div className="whitespace-pre-wrap break-words">{m.text}</div>
        <div
          className={cn(
            "mt-0.5 flex items-center gap-1.5 text-2xs",
            isMember ? "text-muted-foreground" : "text-foreground/60",
          )}
        >
          <span>{relTime(m.sent_at)}</span>
          <span>·</span>
          <span className="font-mono">{m.kind}</span>
          {m.cost_cents > 0 ? (
            <>
              <span>·</span>
              <span>{dollars(m.cost_cents)}</span>
            </>
          ) : null}
          {m.approved_by ? (
            <>
              <span>·</span>
              <span title={`approved by ${m.approved_by}`}>✓ {m.approved_by.split("@")[0]}</span>
            </>
          ) : null}
          {m.system_prompt_summary ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="ml-1 inline-flex cursor-help items-center text-brand/80">
                  <Sparkles className="h-3 w-3" />
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-md">{m.system_prompt_summary}</TooltipContent>
            </Tooltip>
          ) : null}
        </div>
      </div>
    </div>
  );
}
